import { useMemo, useState } from 'react'
import {
  Box,
  Checkbox,
  Chip,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material'
import { MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md'
import { format, isBefore, isToday, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { IMontagemGrupoProduto, IOrderProductHCPFlow, getItensPendentesPedido } from '../../Pages/HCP/CardAssembly/card-assembly.hook'
import { getOrderNsNumber } from '../../utils/billingFlow'

interface IMontagemCartelasTableProps {
  grupos: IMontagemGrupoProduto[]
  loading: boolean
  selectedOrderProductIds: Array<string | number>
  onToggleOrderProductSelection: (orderProductId: string | number, checked: boolean) => void
}

type SortKey = 'prazo' | 'tiragem'
type SortDir = 'asc' | 'desc'

function prazoLabel(prazo: string | null): { label: string; urgente: boolean } {
  if (!prazo) return { label: '---', urgente: false }
  try {
    const date = parseISO(prazo)
    const hoje = new Date()
    hoje.setHours(0, 0, 0, 0)
    const urgente = isBefore(date, hoje) || isToday(date)
    return { label: format(date, 'dd/MM/yy', { locale: ptBR }), urgente }
  } catch {
    return { label: prazo, urgente: false }
  }
}

function getPedidoNumero(item: IOrderProductHCPFlow): string {
  return getOrderNsNumber(item.order ?? undefined) ?? item.order?.nsOrderId ?? '---'
}

function StatusChip({ grupo }: { grupo: IMontagemGrupoProduto }) {
  if (grupo.podeFecharCartela) {
    return (
      <Chip
        label="Pronto para fechar"
        size="small"
        sx={{ bgcolor: 'rgba(132, 168, 2, 0.15)', color: '#4a6000', fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  }
  if (grupo.meiaCartelaDisponivel) {
    const label = grupo.qtdMeiaCartelas === 1 ? 'Meia cartela disponivel' : `${grupo.qtdMeiaCartelas}x meia cartela`
    return (
      <Chip
        label={label}
        size="small"
        sx={{ bgcolor: 'rgba(2, 136, 209, 0.12)', color: '#01579b', fontWeight: 600, fontSize: '0.75rem' }}
      />
    )
  }
  const faltam = grupo.minimoTiragem - grupo.totalTiragem
  return (
    <Chip
      label={`Aguardando ${faltam.toLocaleString('pt-BR')} un`}
      size="small"
      sx={{ bgcolor: 'rgba(255, 193, 7, 0.15)', color: '#7a5800', fontWeight: 600, fontSize: '0.75rem' }}
    />
  )
}

function GrupoRow({
  grupo,
  selectedOrderProductIds,
  onToggleOrderProductSelection,
}: {
  grupo: IMontagemGrupoProduto
  selectedOrderProductIds: Array<string | number>
  onToggleOrderProductSelection: (orderProductId: string | number, checked: boolean) => void
}) {
  const [open, setOpen] = useState(false)
  const progressValue = Math.min((grupo.totalTiragem / grupo.minimoTiragem) * 100, 100)
  const progressColor = grupo.podeFecharCartela ? '#2e7d32' : grupo.meiaCartelaDisponivel ? '#0288d1' : '#ed6c02'
  const rowBg = grupo.podeFecharCartela ? 'rgba(132, 168, 2, 0.05)' : grupo.meiaCartelaDisponivel ? 'rgba(2, 136, 209, 0.04)' : 'inherit'
  const rowHover = grupo.podeFecharCartela ? 'rgba(132, 168, 2, 0.10)' : grupo.meiaCartelaDisponivel ? 'rgba(2, 136, 209, 0.08)' : 'rgba(0,0,0,0.02)'
  const { label: prazoFormatado, urgente: prazoUrgente } = prazoLabel(grupo.prazoMinimo)

  return (
    <>
      <TableRow
        onClick={() => setOpen(!open)}
        sx={{
          '& > *': { borderBottom: 'unset' },
          bgcolor: rowBg,
          cursor: 'pointer',
          '&:hover': { bgcolor: rowHover },
        }}
      >
        <TableCell sx={{ width: 48 }}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setOpen(!open) }}>
            {open ? <MdKeyboardArrowUp size={20} /> : <MdKeyboardArrowDown size={20} />}
          </IconButton>
        </TableCell>

        <TableCell>
          <Typography fontWeight={600} fontSize={14}>{grupo.produto_nome}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14}>{grupo.itens.length}</Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontWeight={600} fontSize={14}>
            {grupo.totalTiragem.toLocaleString('pt-BR')}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              mt: 0.5,
              height: 4,
              borderRadius: 2,
              bgcolor: 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': { bgcolor: progressColor, borderRadius: 2 },
            }}
          />
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14} color="text.secondary">
            {grupo.minimoTiragem.toLocaleString('pt-BR')}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14} color="text.secondary">
            {grupo.meiaCartela ? grupo.meiaCartela.toLocaleString('pt-BR') : '---'}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <Typography fontSize={14} fontWeight={prazoUrgente ? 700 : 400} color={prazoUrgente ? 'error.main' : 'text.primary'}>
            {prazoFormatado}
          </Typography>
        </TableCell>

        <TableCell align="center">
          <StatusChip grupo={grupo} />
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={8} sx={{ py: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ mx: 2, my: 1 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox" />
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>#Pedido</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Cliente</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Tiragem</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Código Fluxo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>C. Produto Fluxo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Prazo Fluxo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Prazo Final Cliente</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Arte Final</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem' }}>Pend. pedido</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {grupo.itens.map((item) => {
                    const itensPendentes = getItensPendentesPedido(item)
                    const { label: prazoFluxoLabel, urgente: prazoFluxoUrgente } = prazoLabel(item.hcpCurrentFlowDeadline ?? null)
                    const { label: prazoClienteLabel, urgente: prazoClienteUrgente } = prazoLabel(item.customerDeadline ?? null)
                    const checked = selectedOrderProductIds.some((id) => String(id) === String(item.id))

                    return (
                      <TableRow key={item.id} hover>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={checked}
                            disabled={Boolean(item.hcpPreServiceOrderId)}
                            onClick={(event) => event.stopPropagation()}
                            onChange={(event) => onToggleOrderProductSelection(item.id, event.target.checked)}
                          />
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{getPedidoNumero(item)}</TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>{item.order?.customer?.name ?? '---'}</TableCell>
                        <TableCell align="center" sx={{ fontSize: '0.85rem' }}>
                          {(Number(item.totalQuantity) || 0).toLocaleString('pt-BR')}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.hcpCurrentFlowDisplayCode ?? item.hcpCurrentStatus?.displayCode ?? '---'}
                            size="small"
                            sx={{ fontSize: '0.70rem', height: 20, fontWeight: 700, bgcolor: 'rgba(21,101,192,0.10)', color: '#1565c0' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={item.productOrderCode ?? '---'}
                            size="small"
                            sx={{ fontSize: '0.70rem', height: 20, fontWeight: 700, bgcolor: 'rgba(0,0,0,0.06)', color: 'text.secondary' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            fontSize="0.85rem"
                            fontWeight={prazoFluxoUrgente ? 700 : 400}
                            color={prazoFluxoUrgente ? 'error.main' : 'text.primary'}
                          >
                            {prazoFluxoLabel}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography
                            fontSize="0.85rem"
                            fontWeight={prazoClienteUrgente ? 700 : 400}
                            color={prazoClienteUrgente ? 'error.main' : 'text.primary'}
                          >
                            {prazoClienteLabel}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem' }}>
                          {item.artLink ? (
                            <Link href={item.artLink} target="_blank" rel="noopener noreferrer" underline="hover">
                              Arte Final
                            </Link>
                          ) : (
                            <Typography component="span" fontSize="0.85rem" color="text.disabled">---</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={itensPendentes === 0 ? 'Completo' : `${itensPendentes} pend.`}
                            size="small"
                            sx={{
                              fontSize: '0.70rem',
                              height: 20,
                              fontWeight: 600,
                              bgcolor: itensPendentes === 0 ? 'rgba(46,125,50,0.12)' : '#fff3e0',
                              color: itensPendentes === 0 ? '#2e7d32' : '#e65100',
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export function MontagemCartelasTable({
  grupos,
  loading,
  selectedOrderProductIds,
  onToggleOrderProductSelection,
}: IMontagemCartelasTableProps) {
  const [sortBy, setSortBy] = useState<SortKey | null>(null)
  const [sortDir, setSortDir] = useState<SortDir>('asc')

  const handleSort = (key: SortKey) => {
    if (sortBy === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc')
    } else {
      setSortBy(key)
      setSortDir('asc')
    }
  }

  const sortedGrupos = useMemo(() => {
    if (!sortBy) return grupos
    return [...grupos].sort((a, b) => {
      let cmp = 0
      if (sortBy === 'prazo') {
        const ap = a.prazoMinimo ?? ''
        const bp = b.prazoMinimo ?? ''
        if (!ap && !bp) cmp = 0
        else if (!ap) cmp = 1
        else if (!bp) cmp = -1
        else cmp = ap.localeCompare(bp)
      } else if (sortBy === 'tiragem') {
        cmp = a.totalTiragem - b.totalTiragem
      }
      return sortDir === 'asc' ? cmp : -cmp
    })
  }, [grupos, sortBy, sortDir])

  if (!loading && grupos.length === 0) {
    return (
      <Box sx={{ py: 8, textAlign: 'center' }}>
        <Typography color="text.secondary">Nenhum produto encontrado.</Typography>
      </Box>
    )
  }

  const prontos = grupos.filter((g) => g.podeFecharCartela).length
  const meia = grupos.filter((g) => g.meiaCartelaDisponivel).length
  const aguardando = grupos.filter((g) => !g.podeFecharCartela && !g.meiaCartelaDisponivel).length

  return (
    <>
      <Box sx={{ px: 2.5, py: 1.5, display: 'flex', gap: 1, flexWrap: 'wrap', borderBottom: '1px solid rgba(0,0,0,0.06)', bgcolor: 'rgba(0,0,0,0.01)' }}>
        <Chip label={`${prontos} pronto${prontos !== 1 ? 's' : ''} para fechar`} size="small" sx={{ bgcolor: 'rgba(132, 168, 2, 0.12)', color: '#4a6000', fontWeight: 700, fontSize: '0.72rem' }} />
        <Chip label={`${meia} com meia cartela`} size="small" sx={{ bgcolor: 'rgba(2, 136, 209, 0.10)', color: '#01579b', fontWeight: 700, fontSize: '0.72rem' }} />
        <Chip label={`${aguardando} aguardando`} size="small" sx={{ bgcolor: 'rgba(255, 193, 7, 0.12)', color: '#7a5800', fontWeight: 700, fontSize: '0.72rem' }} />
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(0,0,0,0.03)' }}>
              <TableCell sx={{ width: 48 }} />
              <TableCell sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Produto</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Pedidos</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                <TableSortLabel active={sortBy === 'tiragem'} direction={sortBy === 'tiragem' ? sortDir : 'asc'} onClick={() => handleSort('tiragem')}>
                  Tiragem Acumulada
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Minimo Cartela</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Meia Cartela</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                <TableSortLabel active={sortBy === 'prazo'} direction={sortBy === 'prazo' ? sortDir : 'asc'} onClick={() => handleSort('prazo')}>
                  Prazo
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedGrupos.map((grupo) => (
              <GrupoRow
                key={grupo.produto_nome}
                grupo={grupo}
                selectedOrderProductIds={selectedOrderProductIds}
                onToggleOrderProductSelection={onToggleOrderProductSelection}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  )
}
